import React from 'react';
import { AlertTriangle, ChevronRight, EyeOff } from 'lucide-react';

/*
 * NeedsAttention — the top panel of the CEO Dashboard (P140 PR A).
 *
 * Presentational only. It renders exactly what reports:ceo:attention returned and
 * derives no figures of its own: a number computed here could disagree with the
 * same number computed on the server, and the one the CEO acts on would be
 * whichever happened to render.
 *
 * ⚠⚠ THREE STATES, NOT TWO. This component must be able to say "I do not know"
 * as clearly as it says "nothing is wrong":
 *
 *   failed    → the panel could not load.            "could not be loaded"
 *   loaded, items[] empty, notTracked[] empty  →     "Nothing needs attention"
 *   loaded, notTracked[] non-empty             →     each one named, in words
 *
 * The middle case is the ONLY one allowed to look calm. A failure that rendered
 * as an empty panel would read as good news, which is #686 exactly: failed is not
 * empty. `failed` is therefore a required prop, not an optional nicety.
 *
 * NO PATIENT NAMES. Every item is a count, a dollar figure and a rep's name.
 * Contact names appear only at the last level of the drill-down, which audits.
 */

const money = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

export default function NeedsAttention({ data, failed, loading, onOpen }) {
  if (failed) {
    return (
      <div data-testid="attention-failed" style={S.failed}>
        <AlertTriangle size={16} />
        <span style={{ marginLeft: 8 }}>
          Needs attention could not be loaded. These checks are unknown right now, not clear.
        </span>
      </div>
    );
  }

  if (!data) {
    return (
      <div data-testid="attention-loading" style={S.muted}>
        {loading ? 'Checking what needs attention…' : null}
      </div>
    );
  }

  const items = Array.isArray(data.items) ? data.items : [];
  const notTracked = Array.isArray(data.notTracked) ? data.notTracked : [];

  return (
    <section data-testid="needs-attention" style={S.wrap}>
      <header style={S.head}>
        <AlertTriangle size={18} color="#B7791F" />
        <h2 style={S.title}>Needs attention</h2>
        {items.length > 0 && <span style={S.badge}>{items.length}</span>}
      </header>

      {items.length === 0 && (
        <div data-testid="attention-empty" style={S.clear}>
          Nothing needs attention right now.
        </div>
      )}

      {items.length > 0 && (
        <ul style={S.list}>
          {items.map((it, i) => (
            <li key={`${it.ruleKey}-${i}`} style={S.row}>
              <button
                type="button"
                data-testid={`attention-item-${it.ruleKey}`}
                onClick={() => typeof onOpen === 'function' && onOpen(it)}
                style={S.rowBtn}
              >
                <span style={S.rowTitle}>{it.title}</span>
                <span style={S.rowRight}>
                  {/*
                    * null dollars is NOT $0 — it means this rule's subject has no
                    * dollar value at all (a lead before any opportunity exists, a
                    * task). Rendering $0 would invite the CEO to read "nothing at
                    * stake" from "we cannot put a number on it".
                    */}
                  <span style={S.dollars} data-testid={`attention-dollars-${it.ruleKey}`}>
                    {it.dollarsAtRisk === null || it.dollarsAtRisk === undefined
                      ? 'no $ value'
                      : `${money(it.dollarsAtRisk)} at risk`}
                  </span>
                  <ChevronRight size={16} color="#ADB5BD" />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {data.truncated && (
        <div style={S.muted} data-testid="attention-truncated">
          Showing the top {items.length} of {data.totalItems} by dollars at risk.
        </div>
      )}

      {notTracked.length > 0 && (
        <div data-testid="attention-not-tracked" style={S.notTracked}>
          <div style={S.notTrackedHead}>
            <EyeOff size={14} color="#6C757D" />
            <span style={{ marginLeft: 6 }}>Not tracked yet</span>
          </div>
          <ul style={S.ntList}>
            {notTracked.map((nt) => (
              <li key={nt.ruleKey} data-testid={`not-tracked-${nt.ruleKey}`} style={S.ntRow}>
                <strong style={S.ntLabel}>{nt.label}</strong>
                {' — '}
                <span style={S.ntMsg}>{nt.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

const S = {
  wrap: { border: '1px solid #F0E3C7', background: '#FFFCF5', borderRadius: 12, padding: 16, marginBottom: 18 },
  head: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 },
  title: { margin: 0, fontSize: 15, fontWeight: 700, color: '#5F4B12' },
  badge: { background: '#B7791F', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 12, fontWeight: 700 },
  list: { listStyle: 'none', margin: 0, padding: 0 },
  row: { borderTop: '1px solid #F2E8D5' },
  rowBtn: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 12, padding: '9px 2px', background: 'none', border: 'none', cursor: 'pointer',
    textAlign: 'left', font: 'inherit', fontSize: 13, color: '#3D3D3D',
  },
  rowTitle: { flex: 1 },
  rowRight: { display: 'flex', alignItems: 'center', gap: 8 },
  dollars: { fontWeight: 700, color: '#7B341E', whiteSpace: 'nowrap' },
  clear: { fontSize: 13, color: '#6C757D', padding: '6px 2px' },
  muted: { fontSize: 12, color: '#ADB5BD', padding: '6px 2px' },
  failed: {
    display: 'flex', alignItems: 'center', padding: 12, borderRadius: 10,
    background: '#FDECEA', color: '#7B241C', fontSize: 13, marginBottom: 18,
  },
  notTracked: { marginTop: 12, paddingTop: 10, borderTop: '1px dashed #E3D7BC' },
  notTrackedHead: { display: 'flex', alignItems: 'center', fontSize: 12, fontWeight: 700, color: '#6C757D', marginBottom: 6 },
  ntList: { listStyle: 'none', margin: 0, padding: 0 },
  ntRow: { fontSize: 12, color: '#6C757D', padding: '3px 0' },
  ntLabel: { color: '#495057' },
  ntMsg: {},
};
